import { useRef, useState, type ElementType } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import Slider, { type CustomArrowProps } from 'react-slick';
import {
  CaretRight,
  CaretLeft,
  Map as MapIcon,
  Credit,
  BookWithApple,
  Calendar3,
  TruckWithExclamation,
  PencilAndPaper,
  Box3,
  BoxWithPencil,
} from '@hungryroot/icons';
import { useGetOrders } from '@hungryroot/orders';
import {
  Heading,
  Paragraph,
  IconButton,
  TransparentButton,
} from '@hungryroot/ramen';
import {
  SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
  SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
  SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
  SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
  SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
} from '@hungryroot/routes';

import styles from './GettingStartHelpCenterSection.module.css';

const slidesToShow = 4;

type GettingStartHelpCenterSectionProps = {
  title: string;
  className?: string;
};

export function GettingStartHelpCenterSection(
  props: GettingStartHelpCenterSectionProps
) {
  const { title, className } = props;

  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const sliderRef = useRef<Slider>(null);
  const cards = useCards();

  if (cards.length === 0) {
    return null;
  }

  const settings = {
    dots: false,
    arrows: false,
    infinite: false,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToShow,
    beforeChange: (_: number, next: number) => {
      setCurrentSlide(next);
    },
  };

  return (
    <div className={clsx(styles['container'], className)}>
      <div className={styles['section-header']}>
        <div>
          <Heading variant="H2" className={styles['section-title']}>
            {title}
          </Heading>
          <Paragraph variant="body">
            Tips to get the most out of your Hungryroot
          </Paragraph>
        </div>
        {cards.length > slidesToShow ? (
          <div className={styles['section-header-arrow-container']}>
            <PrevArrow
              onClick={
                currentSlide === 0
                  ? undefined
                  : () => sliderRef.current?.slickPrev()
              }
            />
            <NextArrow
              onClick={
                cards.length - currentSlide <= slidesToShow
                  ? undefined
                  : () => sliderRef.current?.slickNext()
              }
            />
          </div>
        ) : null}
      </div>

      <Slider {...settings} ref={sliderRef}>
        {cards.map((card) => (
          <Slide key={card.href} {...card} />
        ))}
      </Slider>

      <div className={styles['mobile-slider-container']}>
        {cards.map((card) => (
          <Slide key={card.href} {...card} />
        ))}
      </div>
    </div>
  );
}

type SlideProps = {
  icon: ElementType;
  title: string;
  subtitle: string;
  href: string;
};

function Slide(props: SlideProps) {
  const { icon: Icon, title, subtitle, href } = props;

  const history = useHistory();

  return (
    <TransparentButton
      className={styles.slide}
      onClick={() => history.push(href)}
    >
      <Icon className={styles['slide-icon']} />
      <div>
        <Heading variant="H6" className={styles['slide-title']}>
          {title}
        </Heading>
        <Paragraph variant="caption" className={styles['slide-subtitle']}>
          {subtitle}
        </Paragraph>
      </div>
    </TransparentButton>
  );
}

function NextArrow(props: CustomArrowProps) {
  const { onClick } = props;

  return (
    <IconButton
      icon={<CaretRight />}
      style={onClick ? 'muted' : 'disabled'}
      prominence="secondary"
      size="small"
      ariaLabel="Next slide"
      onClick={onClick}
    />
  );
}

function PrevArrow(props: CustomArrowProps) {
  const { onClick } = props;

  return (
    <IconButton
      style={onClick ? 'muted' : 'disabled'}
      icon={<CaretLeft />}
      prominence="secondary"
      size="small"
      ariaLabel="Previous slide"
      onClick={onClick}
    />
  );
}

type Card = {
  icon: ElementType;
  title: string;
  subtitle: string;
  href: string;
};

const CardToPageMap = new Map<string, Card>([
  [
    SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
    {
      icon: MapIcon,
      title: 'A quick tour',
      subtitle: 'How to order and where to find things.',
      href: SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
    {
      icon: Credit,
      title: 'Shop with credits',
      subtitle: 'How credits work at Hungryroot.',
      href: SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
    {
      icon: Box3,
      title: 'Your first delivery',
      subtitle: 'How and when your delivery will arrive.',
      href: SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
    {
      icon: BookWithApple,
      title: 'Your food profile',
      subtitle: 'How to help us build you a better box each week.',
      href: SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
    {
      icon: Calendar3,
      title: 'Manage your schedule',
      subtitle: 'Make your delivery schedule work for you.',
      href: SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
    {
      icon: TruckWithExclamation,
      title: 'Delivery issues',
      subtitle: "What to do if something's not right.",
      href: SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
    {
      icon: BoxWithPencil,
      title: 'Edit your order',
      subtitle: 'Add, remove, or swap items in your order.',
      href: SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
    },
  ],
  [
    SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
    {
      icon: PencilAndPaper,
      title: 'Edit your plan',
      subtitle: 'Adjust how much food you get each week.',
      href: SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
    },
  ],
]);

const firstOrderPendingCards = [
  SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
];

const firstOrderSubmittedCards = [
  SUPPORT_ARTICLE_QUICK_TOUR_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_FIRST_DELIVERY_ROUTE,
  SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
];

const firstOrderDeliveredCards = [
  SUPPORT_ARTICLE_DELIVERY_ISSUES_ROUTE,
  SUPPORT_ARTICLE_SHOP_WITH_CREDITS_ROUTE,
  SUPPORT_ARTICLE_EDIT_ORDER_ROUTE,
  SUPPORT_ARTICLE_SCHEDULE_MANAGEMENT_ROUTE,
  SUPPORT_ARTICLE_EDIT_PLAN_ROUTE,
  SUPPORT_ARTICLE_FOOD_PROFILE_ROUTE,
];

function useCards() {
  const { isLoading: isCurrentOrderLoading, data: currentOrderList } =
    useGetOrders({
      time_bucket: 'current',
      limit: 1,
    });

  const { isLoading: isPastOrderLoading, data: pastOrderList } = useGetOrders({
    time_bucket: 'past',
    limit: 1,
  });

  if (isCurrentOrderLoading || isPastOrderLoading) {
    return [];
  }

  const currentOrder = currentOrderList?.results[0];
  const pastOrder = pastOrderList?.results[0];

  let cards;

  const isCurrentOrderTheFirstOrder = currentOrder?.sequence === 1;

  if (pastOrder) {
    cards = firstOrderDeliveredCards;
  } else if (isCurrentOrderTheFirstOrder) {
    cards = firstOrderSubmittedCards;
  } else {
    cards = firstOrderPendingCards;
  }

  return cards.map((card) => CardToPageMap.get(card)).filter(Boolean) as Card[];
}
